/** Product and variant payload schemas. */

import { z } from "zod";
import {
  BUNDLE_DEFAULTS,
  DEFAULT_PRODUCT_UNIT,
  PRODUCT_DEFAULTS,
  PRODUCT_KINDS,
  VARIANT_DEFAULTS,
} from "../constants/products";
import { moneySchema, pageQuerySchema, skuSchema, uuidSchema } from "./common";

const productKindValues = [PRODUCT_KINDS.sale, PRODUCT_KINDS.rental, PRODUCT_KINDS.bundle] as const;

export const productKindSchema = z.enum(productKindValues);

export const bundleComponentSchema = z.object({
  component_product_id: uuidSchema,
  component_variant_id: uuidSchema.nullish(),
  quantity: z.coerce
    .number()
    .int()
    .min(1)
    .max(BUNDLE_DEFAULTS.maxComponentQuantity),
});

const bundleComponentsSchema = z
  .array(bundleComponentSchema)
  .max(BUNDLE_DEFAULTS.maxComponents)
  .nullish();

const requireComponentsForBundle = (
  value: { kind?: string; components?: unknown[] | null },
  ctx: z.RefinementCtx,
) => {
  if (value.kind === PRODUCT_KINDS.bundle && (value.components?.length ?? 0) < BUNDLE_DEFAULTS.minComponents) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["components"],
      message: "A bundle needs at least one component.",
    });
  }
  if (value.kind !== PRODUCT_KINDS.bundle && (value.components?.length ?? 0) > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["components"],
      message: "Only bundle products can define components.",
    });
  }
};

const productFields = {
  name: z.string().trim().min(2).max(PRODUCT_DEFAULTS.maxNameLength),
  sku: skuSchema.nullish(),
  description: z.string().trim().max(PRODUCT_DEFAULTS.maxDescriptionLength).nullish(),
  kind: productKindSchema,
  price: moneySchema,
  cost: moneySchema.nullish(),
  stock: z.coerce.number().int().min(0).max(PRODUCT_DEFAULTS.maxStock),
  unit: z.string().trim().min(1).max(16),
  is_active: z.boolean(),
  image_key: z.string().trim().max(512).nullish(),
};

export const createProductSchema = z
  .object({
    ...productFields,
    kind: productFields.kind.default(PRODUCT_KINDS.sale),
    stock: productFields.stock.default(0),
    unit: productFields.unit.default(DEFAULT_PRODUCT_UNIT),
    is_active: productFields.is_active.default(true),
    price: productFields.price,
    components: bundleComponentsSchema,
  })
  .superRefine(requireComponentsForBundle);

export const updateProductSchema = z
  .object({
    name: productFields.name.optional(),
    sku: productFields.sku,
    description: productFields.description,
    kind: productFields.kind.optional(),
    price: productFields.price.optional(),
    cost: productFields.cost,
    stock: productFields.stock.optional(),
    unit: productFields.unit.optional(),
    is_active: productFields.is_active.optional(),
    image_key: productFields.image_key,
    components: bundleComponentsSchema,
  })
  .refine((value) => Object.keys(value).length > 0, { message: "Nothing to update." })
  .superRefine(requireComponentsForBundle);

export const createVariantSchema = z.object({
  name: z.string().trim().min(1).max(VARIANT_DEFAULTS.maxNameLength),
  sku: skuSchema.nullish(),
  price: moneySchema,
  stock: z.coerce.number().int().min(0).max(PRODUCT_DEFAULTS.maxStock).default(0),
  attributes: z.record(z.string().max(40), z.string().max(80)).default({}),
  is_active: z.boolean().default(true),
});

export const updateVariantSchema = z
  .object({
    name: z.string().trim().min(1).max(VARIANT_DEFAULTS.maxNameLength).optional(),
    sku: skuSchema.nullish(),
    price: moneySchema.optional(),
    stock: z.coerce.number().int().min(0).max(PRODUCT_DEFAULTS.maxStock).optional(),
    attributes: z.record(z.string().max(40), z.string().max(80)).optional(),
    is_active: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: "Nothing to update." });

export const productListQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
  kind: productKindSchema.optional(),
  active_only: z.coerce.boolean().optional(),
});
